import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { TREE_CONFIG, COLORS } from '../../utils/constants'

interface TreeStarProps {
  progress: number
}

export function TreeStar({ progress }: TreeStarProps) {
  const starRef = useRef<THREE.Group>(null)
  const glowRef = useRef<THREE.Mesh>(null)
  
  // 星星动画
  useFrame((state) => {
    if (starRef.current) {
      // 旋转动画
      starRef.current.rotation.y += 0.01 * progress
      
      // 浮动动画
      const floatY = Math.sin(state.clock.elapsedTime * 2) * 0.1 * progress
      starRef.current.position.y = TREE_CONFIG.height + 0.5 + floatY
    }
    
    if (glowRef.current) {
      // 光晕脉动
      const scale = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.2
      glowRef.current.scale.setScalar(scale * progress)
    }
  })
  
  // 星星在成形时显示
  const opacity = progress
  const scale = progress
  
  return (
    <group 
      ref={starRef} 
      position={[0, TREE_CONFIG.height + 0.5, 0]}
      scale={scale}
    >
      {/* 主星星 - 增大尺寸，增强发光 */}
      <group scale={1.5}>
        {/* 上尖 */}
        <mesh position={[0, 0.4, 0]}>
          <coneGeometry args={[0.3, 0.6, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 下尖 */}
        <mesh position={[0, -0.4, 0]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.3, 0.6, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 左尖 */}
        <mesh position={[-0.4, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <coneGeometry args={[0.25, 0.5, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 右尖 */}
        <mesh position={[0.4, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <coneGeometry args={[0.25, 0.5, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 前尖 */}
        <mesh position={[0, 0, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.25, 0.5, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 后尖 */}
        <mesh position={[0, 0, -0.4]} rotation={[-Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.25, 0.5, 4]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.9}
            roughness={0.1}
            emissive={COLORS.gold.bright}
            emissiveIntensity={1.5 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
        
        {/* 中心球 - 更亮 */}
        <mesh>
          <sphereGeometry args={[0.25, 16, 16]} />
          <meshStandardMaterial
            color={COLORS.gold.bright}
            metalness={0.95}
            roughness={0.05}
            emissive={COLORS.gold.bright}
            emissiveIntensity={2.0 * progress}
            transparent
            opacity={opacity}
          />
        </mesh>
      </group>
      
      {/* 内层光晕 */}
      <mesh ref={glowRef} scale={1.2}>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshBasicMaterial
          color={COLORS.gold.bright}
          transparent
          opacity={0.3 * progress}
          side={THREE.BackSide}
        />
      </mesh>
      
      {/* 外层光晕 - 更大更柔和 */}
      <mesh scale={2.0}>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshBasicMaterial
          color={COLORS.gold.bright}
          transparent
          opacity={0.1 * progress}
          side={THREE.BackSide}
        />
      </mesh>
      
      {/* 点光源 - 增强亮度 */}
      <pointLight
        color={COLORS.gold.bright}
        intensity={4 * progress}
        distance={8}
        decay={2}
      />
    </group>
  )
}